import { 
  signInAnonymously as firebaseSignInAnonymously,
  signInWithPopup,
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  GoogleAuthProvider,
  UserCredential,
  User as FirebaseUser
} from 'firebase/auth'
import { 
  doc, 
  updateDoc, 
  serverTimestamp, 
  setDoc, 
  getDoc, 
  Timestamp,
  Firestore 
} from 'firebase/firestore'
import { db as firebaseDb, auth } from '../utils/firebase'
import { AppError } from '../utils/errors'
import { User, defaultUserPreferences } from '../types/user'
import { appAccessService } from './appAccessService'

// Type assertion for db
const db = firebaseDb as Firestore;

// Add timezone cache constants
const TIMEZONE_CACHE_KEY = 'user_timezone_last_check'
const TIMEZONE_CHECK_INTERVAL = 24 * 60 * 60 * 1000 // 24 hours in milliseconds
// Helper function to generate default name
function generateDefaultName(email: string | null, isAnonymous: boolean): string {
  if (isAnonymous) return 'Guest User'
  if (!email) return 'Tydy User'
  
  // Use email prefix before @ as name, capitalize first letter
  const namePart = email.split('@')[0]
  return namePart
    .split(/[._-]/) // Split on common email separators
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Add this helper function at the top of the file
function getDefaultTimezone(): string {
  try {
    // Get timezone from browser
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezone || 'UTC';
  } catch (error) {
    console.warn('Failed to get timezone:', error);
    return 'UTC';
  }
}

async function createUserProfile(userId: string, userData: Partial<FirebaseUser> & { isAnonymous?: boolean }) {
  const userRef = doc(db, 'users', userId)
  
  if (!(await getDoc(userRef)).exists()) {
    const defaultName = generateDefaultName(userData.email || null, userData.isAnonymous || false)
    
    const newUser = {
      id: userId,
      email: userData.email || null,
      name: userData.displayName || defaultName,
      displayName: userData.displayName || defaultName,
      photoURL: userData.photoURL || null,
      isAnonymous: userData.isAnonymous || false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      preferences: defaultUserPreferences,
      version: 1,
      accountStatus: 'active' as const,
      emailVerified: userData.emailVerified || false,
      language: navigator.language.split('-')[0] || 'en', // Use browser language
      timezone: getDefaultTimezone(), // Use browser timezone
      bio: null,
      location: null,
      website: null,
    }

    await setDoc(userRef, newUser)
  }
}

// Update the ensureUserTimezone function
async function ensureUserTimezone(userId: string) {
  try {
    // Check local storage for last update time
    const lastCheck = localStorage.getItem(TIMEZONE_CACHE_KEY)
    const now = Date.now()
    
    if (lastCheck) {
      const timeSinceLastCheck = now - parseInt(lastCheck)
      // If checked within last 24 hours, skip
      if (timeSinceLastCheck < TIMEZONE_CHECK_INTERVAL) {
        return
      }
    }

    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)
    
    if (userDoc.exists()) {
      const userData = userDoc.data()
      const browserTimezone = getDefaultTimezone()
      
      // Only update if timezone is UTC or different from browser timezone
      if (userData.timezone === 'UTC' || userData.timezone !== browserTimezone) {
        await updateDoc(userRef, {
          timezone: browserTimezone,
          updatedAt: serverTimestamp()
        })
      }

      // Update last check time
      localStorage.setItem(TIMEZONE_CACHE_KEY, now.toString())
    }
  } catch (error) {
    console.warn('Failed to update timezone:', error)
  }
}

async function updateLastLogin(userId: string) {
  try {
    const userRef = doc(db, 'users', userId)
    await updateDoc(userRef, {
      lastLogin: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    
    // Check and update timezone if needed
    await ensureUserTimezone(userId)
    
  } catch (error) {
    console.error('Error updating last login:', error)
    // Non-critical error, don't throw
  }
}

export const authService = {
  signInAnonymously: async (): Promise<UserCredential> => {
    try {
      appAccessService.requirePrivateAppAccess({ isAnonymous: true })
      const result = await firebaseSignInAnonymously(auth)
      await createUserProfile(result.user.uid, {
        isAnonymous: true,
        email: null,
        displayName: 'Guest User',
        photoURL: '/icons/default-avatar.png'
      })
      await updateLastLogin(result.user.uid) // This will now also check timezone
      await authService.ensureUserDocument(result.user)
      return result
    } catch (error) {
      console.error('[AuthService] Anonymous sign in failed:', error)
      throw new AppError('ANONYMOUS_SIGN_IN_FAILED', 'Failed to sign in as guest')
    }
  },

  signInWithGoogle: async () => {
    try {
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)
      appAccessService.requirePrivateAppAccess(result.user)
      try {
        await createUserProfile(result.user.uid, result.user)
      } catch (error) {
        if (!(error instanceof AppError && error.code === 'USER_EXISTS')) {
          throw error
        }
      }
      await updateLastLogin(result.user.uid) // This will now also check timezone
      await authService.ensureUserDocument(result.user)
      return result
    } catch (error) {
      console.error('[AuthService] Google sign in failed:', error)
      throw new AppError('GOOGLE_SIGN_IN_FAILED', 'Failed to sign in with Google')
    }
  },

  signInWithEmailAndPassword: async (email: string, password: string) => {
    try {
      const result = await firebaseSignInWithEmailAndPassword(auth, email, password)
      appAccessService.requirePrivateAppAccess(result.user)
      await updateLastLogin(result.user.uid) // This will now also check timezone
      await authService.ensureUserDocument(result.user)
      return result
    } catch (error) {
      console.error('[AuthService] Email/password sign in failed:', error)
      throw new AppError('EMAIL_SIGN_IN_FAILED', 'Failed to sign in with email/password')
    }
  },

  async ensureUserDocument(firebaseUser: FirebaseUser): Promise<User> {
    try {
      appAccessService.requirePrivateAppAccess(firebaseUser)
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      const now = Timestamp.now();

      if (!userDoc.exists()) {
        // Create new user document
        const newUser: User = {
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          emailVerified: firebaseUser.emailVerified,
          displayName: firebaseUser.displayName || 'User',
          photoURL: firebaseUser.photoURL || undefined,
          accountStatus: 'active',
          language: 'en',
          timezone: 'UTC',
          preferences: defaultUserPreferences,
          createdAt: now,
          updatedAt: now,
          lastLogin: now
        };

        await setDoc(doc(db, 'users', firebaseUser.uid), newUser);
        return newUser;
      }

      // Update existing user
      const userData = userDoc.data() as User;
      
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        lastLogin: now,
        updatedAt: now
      });

      return {
        ...userData,
        id: firebaseUser.uid,
        lastLogin: now,
        updatedAt: now
      };
    } catch (error) {
      console.error('[AuthService] Error ensuring user document:', error);
      throw error;
    }
  },

  ensureUserTimezone // Export the new function
} 
